import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { INSIGHT_CATEGORIES } from "@/data/content";

const EMPTY_ARTICLE = { title: "", category: INSIGHT_CATEGORIES[0], excerpt: "", body: "", published: false };

const inputClass =
  "w-full border border-ink/20 bg-white px-4 py-3 text-sm focus:outline-none focus:border-burgundy transition-colors";

const ArticleEditor = ({ article, onSave, onCancel }) => {
  const [form, setForm] = useState(article);
  const [saving, setSaving] = useState(false);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-ink/15 bg-white p-8 space-y-5" data-testid="article-editor">
      <div>
        <label className="block text-xs uppercase tracking-[0.2em] font-medium text-ink/60 mb-1">Title *</label>
        <input type="text" value={form.title} onChange={set("title")} required className={inputClass} data-testid="article-title-input" />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-[0.2em] font-medium text-ink/60 mb-1">Category</label>
        <select value={form.category} onChange={set("category")} className={inputClass} data-testid="article-category-select">
          {INSIGHT_CATEGORIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs uppercase tracking-[0.2em] font-medium text-ink/60 mb-1">Excerpt</label>
        <textarea value={form.excerpt} onChange={set("excerpt")} rows={2} className={inputClass} data-testid="article-excerpt-input" />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-[0.2em] font-medium text-ink/60 mb-1">
          Body <span className="normal-case tracking-normal text-ink/40">(blank line between paragraphs)</span>
        </label>
        <textarea value={form.body} onChange={set("body")} rows={12} className={inputClass} data-testid="article-body-input" />
      </div>
      <label className="flex items-center gap-3 cursor-pointer">
        <input type="checkbox" checked={form.published} onChange={set("published")} className="h-4 w-4 accent-[#610C27]" data-testid="article-published-checkbox" />
        <span className="text-sm text-ink/70">Published (visible on the public site)</span>
      </label>
      <div className="flex gap-4 pt-2">
        <button
          type="submit" disabled={saving} data-testid="article-save-btn"
          className="bg-burgundy text-cream text-xs uppercase tracking-[0.2em] font-bold px-8 py-3.5 hover:bg-ink transition-colors disabled:opacity-60 inline-flex items-center gap-2"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          Save Article
        </button>
        <button type="button" onClick={onCancel} data-testid="article-cancel-btn"
          className="border border-ink/25 text-xs uppercase tracking-[0.2em] font-semibold px-8 py-3.5 hover:border-ink transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
};

const InsightsTab = () => {
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | article object
  const [loading, setLoading] = useState(true);

  const load = () =>
    api.get("/admin/insights").then((res) => setArticles(res.data)).catch(() => {}).finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const save = async (form) => {
    try {
      if (editing === "new") {
        await api.post("/admin/insights", form);
        toast.success("Article created");
      } else {
        await api.put(`/admin/insights/${editing._id || editing.id}`, form);
        toast.success("Article updated");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try {
      await api.delete(`/admin/insights/${a._id || a.id}`);
      toast.success("Article deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (editing) {
    return (
      <ArticleEditor
        article={editing === "new" ? EMPTY_ARTICLE : editing}
        onSave={save}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div data-testid="insights-tab">
      <button
        onClick={() => setEditing("new")} data-testid="new-article-btn"
        className="bg-burgundy text-cream text-xs uppercase tracking-[0.2em] font-bold px-6 py-3.5 hover:bg-ink transition-colors inline-flex items-center gap-2"
      >
        <Plus className="h-4 w-4" /> New Article
      </button>
      {loading ? (
        <p className="mt-10 text-sm text-ink/50">Loading…</p>
      ) : articles.length === 0 ? (
        <p className="mt-10 text-sm text-ink/50" data-testid="no-articles-note">
          No articles yet. Published articles will appear on the public Insights page.
        </p>
      ) : (
        <div className="mt-8 border-t border-ink/15">
          {articles.map((a) => (
            <div key={a._id || a.id} className="flex items-center gap-4 py-5 border-b border-ink/10" data-testid={`admin-article-${a.slug}`}>
              <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${a.published ? "border-teal text-teal" : "border-ink/30 text-ink/40"}`}>
                {a.published ? "Published" : "Draft"}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-lg text-ink truncate">{a.title}</p>
                <p className="text-xs text-ink/40">{a.category}</p>
              </div>
              <button onClick={() => setEditing(a)} aria-label={`Edit ${a.title}`} data-testid={`edit-${a.slug}`} className="p-2 text-ink/50 hover:text-burgundy transition-colors">
                <Pencil className="h-4 w-4" />
              </button>
              <button onClick={() => remove(a)} aria-label={`Delete ${a.title}`} data-testid={`delete-${a.slug}`} className="p-2 text-ink/50 hover:text-destructive transition-colors">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const BioTab = () => {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/content/bio").then((res) => setText(res.data.text)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content/bio", { text });
      toast.success("Biography updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <p className="text-sm text-ink/50">Loading…</p>;

  return (
    <div className="border border-ink/15 bg-white p-8" data-testid="bio-tab">
      <p className="text-sm text-ink/60 leading-relaxed">
        This text appears in the Principal Consultant section of the Who We Are page. Separate paragraphs with a
        blank line. The page updates immediately — no redesign required.
      </p>
      <textarea
        value={text} onChange={(e) => setText(e.target.value)} rows={10}
        className={`${inputClass} mt-6`} data-testid="bio-textarea"
      />
      <button
        onClick={save} disabled={saving} data-testid="bio-save-btn"
        className="mt-6 bg-burgundy text-cream text-xs uppercase tracking-[0.2em] font-bold px-8 py-3.5 hover:bg-ink transition-colors disabled:opacity-60 inline-flex items-center gap-2"
      >
        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
        Save Biography
      </button>
    </div>
  );
};

const InquiriesTab = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/inquiries").then((res) => setInquiries(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-sm text-ink/50">Loading…</p>;

  if (inquiries.length === 0) {
    return (
      <p className="text-sm text-ink/50" data-testid="no-inquiries-note">
        No inquiries yet. Contact form submissions are also emailed to mku@mosaicons.ca.
      </p>
    );
  }

  return (
    <div className="border-t border-ink/15" data-testid="inquiries-tab">
      {inquiries.map((q) => (
        <div key={q._id || q.id} className="py-6 border-b border-ink/10" data-testid={`inquiry-${q._id || q.id}`}>
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <p className="font-semibold text-ink">{q.name}</p>
            {q.organization && <p className="text-sm text-ink/60">{q.organization}</p>}
            <p className="text-xs text-burgundy uppercase tracking-[0.12em]">{q.area_of_interest}</p>
            <p className="text-xs text-ink/40 ml-auto">
              {new Date(q.created_at).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" })}
            </p>
          </div>
          <p className="mt-1 text-sm text-ink/60">
            <a href={`mailto:${q.email}`} className="underline hover:text-burgundy">{q.email}</a>
            {q.phone && <> · {q.phone}</>}
          </p>
          <p className="mt-2 text-sm leading-relaxed text-ink/75 whitespace-pre-wrap">{q.message}</p>
        </div>
      ))}
    </div>
  );
};

const TABS = [
  { key: "insights", label: "Insights Articles" },
  { key: "bio", label: "Principal Biography" },
  { key: "inquiries", label: "Inquiries" },
];

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("insights");

  useEffect(() => {
    if (user === false) navigate("/admin", { replace: true });
  }, [user, navigate]);

  if (!user) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-burgundy" />
      </div>
    );
  }

  const handleLogout = async () => {
    await logout();
    navigate("/admin", { replace: true });
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="bg-white border-b border-ink/10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <div className="flex items-center gap-6">
            <span className="text-xs text-ink/50 hidden sm:block" data-testid="admin-user-email">{user.email}</span>
            <Link to="/" className="text-xs uppercase tracking-[0.15em] text-ink/60 hover:text-burgundy link-underline" data-testid="admin-view-site">
              View Site
            </Link>
            <button onClick={handleLogout} className="text-xs uppercase tracking-[0.15em] text-burgundy link-underline" data-testid="admin-logout-btn">
              Sign Out
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-12">
        <h1 className="font-serif text-4xl tracking-tight text-ink">Content Management</h1>
        <div className="mt-8 flex gap-1 border-b border-ink/15" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key} role="tab" aria-selected={tab === t.key}
              onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold transition-colors border-b-2 -mb-px ${
                tab === t.key ? "border-burgundy text-burgundy" : "border-transparent text-ink/50 hover:text-ink"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="mt-10">
          {tab === "insights" && <InsightsTab />}
          {tab === "bio" && <BioTab />}
          {tab === "inquiries" && <InquiriesTab />}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
